/**
 * The shared look of a wait for the model (outside the landing, whose wait is `wait-moment.ts`): a skeleton
 * line or card with a warm light sweeping across it, a preparing state for a small button (a lamp-glow ring and
 * three soft dots), and a stepped caption that says what is being made, one line every few seconds with a fade.
 * None of them claims progress. The styles are `.wait-*` in `styles.css`; under reduced motion the sweep, glow and
 * dots stand still while the captions and skeletons still show. The timing is `wait-steps.ts`.
 */
import { html, LitElement, nothing, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { t } from '../i18n';
import { captionAt, SKELETON_CARDS, type PlayState } from './wait-steps';

export { playState, type PlayState } from './wait-steps';
import { LONG_WAIT_MS } from './wait-lines';

/**
 * Captions in turn: `lines` one every `CAPTION_STEP_MS`, holding on the last, each fading in; with `long`, one more
 * line once the wait passes `LONG_WAIT_MS`. A status region, so a screen reader hears each line.
 */
@customElement('wait-caption')
export class WaitCaption extends LitElement {
  @property({ attribute: false }) lines: readonly string[] = [];
  @property() long = '';
  @state() private at = 0;
  @state() private late = false;
  private started = 0;
  private timer: ReturnType<typeof setInterval> | undefined;

  createRenderRoot() { return this; }

  connectedCallback() {
    super.connectedCallback();
    this.started = Date.now();
    this.at = 0;
    this.late = false;
    this.timer = setInterval(() => {
      const elapsed = Date.now() - this.started;
      const at = Math.max(0, captionAt(elapsed, this.lines.length));
      if (at !== this.at) this.at = at;
      if (this.long && !this.late && elapsed >= LONG_WAIT_MS) this.late = true;
    }, 250);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearInterval(this.timer);
  }

  render() {
    const line = this.lines[this.at] ?? '';
    return html`<p class="wait-caption" role="status" aria-live="polite">
        ${line ? html`<span class="wait-caption-line" data-step=${this.at}>${line}</span>` : nothing}
      </p>
      ${this.late ? html`<p class="wait-caption-long muted small" role="status">${this.long}</p>` : nothing}`;
  }

  protected updated(changed: Map<string, unknown>) {
    // A new line restarts its fade: the span is kept by Lit, so its animation is restarted by hand.
    if (changed.has('at')) {
      const span = this.querySelector<HTMLElement>('.wait-caption-line');
      if (span) {
        span.classList.remove('wait-fade');
        void span.offsetWidth;
        span.classList.add('wait-fade');
      }
    }
  }
}

/** Three soft dots, with a spoken name for them. */
export function waitDots(label = t('wait.voice')): TemplateResult {
  return html`<span class="wait-dots" aria-hidden="true"><i></i><i></i><i></i></span><span class="sr-only">${label}</span>`;
}

/**
 * A primary play button whose size never moves between its states: both labels size it through its
 * pseudo-elements (`.wait-btn` in the CSS), so only the label on screen is in its text. Preparing shows the
 * dots and the lamp-glow ring; a tap then stops, like Stop.
 */
export function playButton(state: PlayState, onClick: () => void, rest = t('lesson.play'), playing = t('lesson.stopPlay')): TemplateResult {
  const preparing = state === 'preparing';
  return html`<button class="btn primary wait-btn ${preparing ? 'preparing' : ''}" data-rest=${rest} data-playing=${playing}
    aria-busy=${preparing ? 'true' : 'false'} @click=${onClick}><span class="wait-btn-in">${
      preparing ? waitDots() : state === 'playing' ? playing : rest}</span></button>`;
}

/**
 * While a pronunciation check listens: the line it listens for with a soft light running along it (none on a card
 * that hides the word until it is said), and what is happening.
 */
export function pronWait(expected = ''): TemplateResult {
  return html`<div class="wait-pron">
    ${expected ? html`<bdi class="ca wait-sweep-text" dir="auto">${expected}</bdi>` : nothing}
    <wait-caption .lines=${[t('wait.pron')]}></wait-caption>
  </div>`;
}

/** A skeleton line: a faint bar with the warm sweep, `width` a share of its row. */
export function skeletonLine(width = 1, cls = ''): TemplateResult {
  return html`<span class="wait-skel ${cls}" style="width:${Math.round(width * 100)}%" aria-hidden="true"></span>`;
}

/**
 * While a question pack is written (practice's first open, the landing's first lesson): what is being made, then
 * skeleton cards, each with one of the moment's own `words` as a faint chip (at least 3 cards). `cls` names the
 * wait for its screen.
 */
export function packWait(words: readonly string[], cls = ''): TemplateResult {
  const cards = Array.from({ length: Math.max(3, Math.min(SKELETON_CARDS, words.length)) }, (_, k) => words[k] ?? '');
  return html`<div class="wait-late ${cls}" aria-busy="true">
    <wait-caption .lines=${[t('wait.practice.words'), t('wait.practice.lines')]}></wait-caption>
    <div class="wait-cards" style="margin-top:10px">
      ${cards.map((word, k) => html`<div class="card wait-card">
        ${word ? html`<bdi class="wait-chip ca" dir="auto">${word}</bdi>` : nothing}
        ${skeletonLine(k % 2 ? 0.82 : 0.94)}${skeletonLine(k % 2 ? 0.5 : 0.62)}
      </div>`)}
    </div>
  </div>`;
}
